import { Block } from '../block.js'
import { TextBlock } from './text-block.js'
import type { TsCodeType, EventType, NameAndType } from '../../types.js'
import {
	jsonClass,
	jsonProperty,
	jsonArrayProperty,
} from '@sumbricht/json-class-serializer'
import { ScreenEditorInput } from '../decorators/screen-editor-input.js'
import { Expression } from '../../data/text-or-code.js'
import { Parameter } from '../../script/parameter.js'

export const DraggableBlockAxisLocks = {
	'': 'none',
	x: 'X axis',
	y: 'Y axis',
}
export type DraggableBlockAxisLock = keyof typeof DraggableBlockAxisLocks

@jsonClass('DraggableBlock')
export class DraggableBlock extends Block {
	@jsonProperty(Expression)
	@ScreenEditorInput<DraggableBlock>({
		inputType: 'code',
		order: 1,
		label: 'Associated data',
		codeLanguage: 'ts',
		tsReturnType: () => 'any',
		editorSize: 'singleline',
		isBinding: false,
		isMandatory: () => true,
	})
	dataExpression: Expression = new Expression('')

	@jsonProperty(String)
	@ScreenEditorInput({
		inputType: 'dropdown',
		order: 2,
		label: 'Lock movement to axis',
		options: Object.entries(DraggableBlockAxisLocks),
	})
	axisLock: DraggableBlockAxisLock = ''

	constructor(init: Partial<DraggableBlock> = {}) {
		super()
		this.init(init)
	}

	static createNewForEditor(): Block {
		return new this({
			children: [new TextBlock({ text: 'Text' })],
		})
	}

	get allowedEventTypes(): EventType[] {
		return [
			{
				name: 'cdkDragDropped',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragDrop<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragEnded',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragEnd',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragEntered',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragEnter<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragExited',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragExit<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragMoved',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragMove<any>',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragReleased',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragRelease',
						defaultExpression: '$event',
					}),
				],
			},
			{
				name: 'cdkDragStarted',
				parameters: [
					new Parameter({
						name: 'event',
						type: 'Sys.Types.CdkDragStart',
						defaultExpression: '$event',
					}),
				],
			},
		]
	}

	getPreviewDirection(): 'row' | 'column' {
		return this.parent?.getPreviewDirection() ?? 'row'
	}

	getEditorCategory(): string {
		return 'Others'
	}
}
